import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateWebsiteMarketingTagRequestSchema,
  type CreateWebsiteMarketingTagRequest,
  MARKETING_TAG_TYPES,
  UpdateWebsiteMarketingTagRequestSchema,
  type UpdateWebsiteMarketingTagRequest,
  type WebsiteMarketingTagDetail,
  type WebsiteMarketingTagListItem,
} from '@utazzo/shared';
import { UzButton, UzDrawer, UzForm, UzFormField, UzInput, UzSelect, UzTextarea } from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateMarketingTag, useUpdateMarketingTag } from '../use-marketing-tags';

type TagType = (typeof MARKETING_TAG_TYPES)[number];

interface MarketingTagFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  tag?: WebsiteMarketingTagDetail | WebsiteMarketingTagListItem | null;
  /** Pre-selected type for create (from the active list filter). */
  defaultType: TagType;
}

const TYPE_OPTIONS = MARKETING_TAG_TYPES.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const buildCreateDefaults = (type: TagType): CreateWebsiteMarketingTagRequest => ({
  type,
  name: '',
  description: '',
  isActive: true,
});

/** Shared field set. Reads the active form from context (UzForm = FormProvider). */
const TagFields = ({
  pending,
  lockType,
}: {
  pending: boolean;
  lockType: boolean;
}): React.JSX.Element => {
  return (
    <>
      <UzFormField name="type">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Type"
            required
            options={TYPE_OPTIONS}
            value={(field.value as string) || 'theme'}
            onValueChange={field.onChange}
            error={error}
            disabled={pending || lockType}
          />
        )}
      </UzFormField>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="Honeymoon"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="description">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Description"
            hint="Optional."
            rows={3}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Status"
            required
            options={STATUS_OPTIONS}
            value={field.value === false ? 'inactive' : 'active'}
            onValueChange={(v) => field.onChange(v === 'active')}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

export const MarketingTagFormDrawer = ({
  open,
  onOpenChange,
  mode,
  tag,
  defaultType,
}: MarketingTagFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createTag = useCreateMarketingTag();
  const updateTag = useUpdateMarketingTag();
  const pending = createTag.isPending || updateTag.isPending;

  const createForm = useForm<CreateWebsiteMarketingTagRequest>({
    resolver: zodResolver(CreateWebsiteMarketingTagRequestSchema),
    defaultValues: buildCreateDefaults(defaultType),
  });
  const updateForm = useForm<UpdateWebsiteMarketingTagRequest>({
    resolver: zodResolver(UpdateWebsiteMarketingTagRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(buildCreateDefaults(defaultType));
    } else if (tag) {
      const d = tag as WebsiteMarketingTagDetail;
      updateForm.reset({
        type: (d.type as TagType) ?? defaultType,
        name: d.name,
        description: d.description ?? '',
        isActive: d.isActive,
      });
    }
  }, [open, isCreate, tag, defaultType, createForm, updateForm]);

  const emptyToNull = (v?: string | null): string | null => (v && v.trim() ? v : null);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createTag.mutateAsync({
      ...values,
      description: emptyToNull(values.description),
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!tag) return;
    await updateTag.mutateAsync({
      id: tag.id,
      payload: {
        ...values,
        description: emptyToNull(values.description),
      },
    });
    onOpenChange(false);
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add tag' : `Edit ${tag?.name ?? 'tag'}`}
      description="Theme / collection tag."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          <TagFields pending={pending} lockType={false} />
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          <TagFields pending={pending} lockType />
        </UzForm>
      )}
    </UzDrawer>
  );
};
